import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Group / team admin (§3.15, §7.7). Agent-only (route guard). A master list on the
// left; selecting a group opens a panel to rename it and add/remove agent members.
// Tickets are routed to a group from the ticket sidebar / triage, not here.
class PageGroups extends StoreMixin(LitElement) {
  static properties = {
    _groups: { state: true },
    _selected: { state: true },   // full group detail (with members)
    _loading: { state: true },
    _newName: { state: true },
    _renameName: { state: true },
    _memberQuery: { state: true },
    _memberResults: { state: true }
  }

  static styles = css`
    :host { display: block; }
    h1 { color: var(--bx-brand); margin: 0 0 0.25rem; font-size: 1.4rem; }
    p.lede { color: var(--bx-muted); margin: 0 0 1rem; }
    .layout { display: grid; grid-template-columns: 20rem 1fr; gap: 1rem; align-items: start; }
    @media (max-width: 720px) { .layout { grid-template-columns: 1fr; } }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); overflow: hidden; }
    .row { padding: 0.7rem 0.9rem; border-bottom: 1px solid var(--bx-border); cursor: pointer; }
    .row:last-child { border-bottom: none; }
    .row:hover, .row.active { background: var(--bx-bg); }
    .name { font-weight: 600; color: var(--bx-ink); }
    .count { color: var(--bx-muted); font-size: 0.8rem; }
    .create { display: flex; gap: 0.4rem; padding: 0.7rem 0.9rem; border-top: 1px solid var(--bx-border); }
    input { flex: 1; box-sizing: border-box; font: inherit; padding: 0.45rem 0.5rem;
            border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); }
    button { font: inherit; cursor: pointer; padding: 0.4rem 0.7rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.85rem; }
    button.primary { background: var(--bx-accent); color: var(--bx-accent-ink); border-color: transparent; font-weight: 700; }
    button:disabled { opacity: 0.5; cursor: default; }
    .rename { display: flex; gap: 0.4rem; }
    .panel { padding: 1.1rem; }
    .panel h2 { margin: 0 0 0.8rem; font-size: 1.1rem; color: var(--bx-ink); }
    .member { display: flex; justify-content: space-between; align-items: center; padding: 0.35rem 0; font-size: 0.9rem; }
    .member button { padding: 0.1rem 0.45rem; }
    .field { margin: 0.6rem 0; }
    .lbl { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bx-muted); margin-bottom: 0.3rem; }
    .results { border: 1px solid var(--bx-border); border-radius: var(--bx-radius); margin-top: 0.3rem; }
    .results div { padding: 0.35rem 0.5rem; cursor: pointer; font-size: 0.85rem; }
    .results div:hover { background: var(--bx-bg); }
    .danger { color: #dc2626; }
    .empty, .loading { padding: 2.5rem; text-align: center; color: var(--bx-muted); }
  `

  constructor() {
    super()
    this._groups = []
    this._selected = null
    this._loading = true
    this._newName = ''
    this._renameName = ''
    this._memberQuery = ''
    this._memberResults = []
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  async _load() {
    this._loading = true
    try {
      const { groups } = await api.get('/groups')
      this._groups = groups
    } catch (err) {
      toast(err.message || 'Failed to load groups', 'error')
    } finally {
      this._loading = false
    }
  }

  async _select(id) {
    try {
      this._selected = await api.get(`/groups/${id}`)
      this._renameName = this._selected.name
      this._memberQuery = ''
      this._memberResults = []
    } catch (err) {
      toast(err.message || 'Failed to load group', 'error')
    }
  }

  async _rename() {
    const name = this._renameName.trim()
    if (!name || name === this._selected.name) return
    try {
      this._selected = await api.patch(`/groups/${this._selected.id}`, { name })
      this._renameName = this._selected.name
      await this._load()
      toast('Group renamed', 'success')
    } catch (err) {
      toast(err.message || 'Failed to rename group', 'error')
    }
  }

  async _create() {
    const name = this._newName.trim()
    if (!name) return
    try {
      const group = await api.post('/groups', { name })
      this._newName = ''
      await this._load()
      this._selected = group
    } catch (err) {
      toast(err.message || 'Failed to create group', 'error')
    }
  }

  async _archive() {
    if (!confirm(`Archive group “${this._selected.name}”? Tickets keep their history but the queue disappears.`)) return
    try {
      await api.delete(`/groups/${this._selected.id}`)
      this._selected = null
      await this._load()
    } catch (err) {
      toast(err.message || 'Failed to archive group', 'error')
    }
  }

  async _searchMember(e) {
    this._memberQuery = e.target.value
    if (this._memberQuery.trim().length < 2) { this._memberResults = []; return }
    try { this._memberResults = await api.get(`/users?query=${encodeURIComponent(this._memberQuery.trim())}&role=agent`) }
    catch { this._memberResults = [] }
  }

  async _addMember(userId) {
    this._memberQuery = ''
    this._memberResults = []
    try {
      this._selected = await api.post(`/groups/${this._selected.id}/members`, { userId })
      await this._load()
    } catch (err) {
      toast(err.message || 'Failed to add member', 'error')
    }
  }

  async _removeMember(userId) {
    try {
      this._selected = await api.delete(`/groups/${this._selected.id}/members/${userId}`)
      await this._load()
    } catch (err) {
      toast(err.message || 'Failed to remove member', 'error')
    }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    return html`
      <h1>Groups</h1>
      <p class="lede">Agent teams. Route tickets to a group queue from a ticket or the triage view.</p>
      <div class="layout">
        <div class="card">
          ${this._groups.length ? this._groups.map(g => html`
            <div class="row ${this._selected?.id === g.id ? 'active' : ''}" @click=${() => this._select(g.id)}>
              <div class="name">${g.name}</div>
              <div class="count">${g.memberCount} member${g.memberCount === 1 ? '' : 's'}</div>
            </div>`) : html`<div class="empty">No groups yet.</div>`}
          <div class="create">
            <input placeholder="New group name…" .value=${this._newName}
              @input=${e => (this._newName = e.target.value)}
              @keydown=${e => e.key === 'Enter' && this._create()} />
            <button class="primary" @click=${this._create}>Add</button>
          </div>
        </div>

        ${this._selected ? html`
          <div class="card panel">
            <div class="field">
              <div class="lbl">Name</div>
              <div class="rename">
                <input .value=${this._renameName}
                  @input=${e => (this._renameName = e.target.value)}
                  @keydown=${e => e.key === 'Enter' && this._rename()} />
                <button class="primary" ?disabled=${!this._renameName.trim() || this._renameName.trim() === this._selected.name}
                  @click=${this._rename}>Rename</button>
              </div>
            </div>
            <div class="field">
              <div class="lbl">Members</div>
              ${this._selected.members.length ? this._selected.members.map(m => html`
                <div class="member">
                  <span>${m.name || m.email}</span>
                  <button @click=${() => this._removeMember(m.id)}>Remove</button>
                </div>`) : html`<div class="count">No members yet.</div>`}
            </div>
            <div class="field">
              <div class="lbl">Add agent</div>
              <input placeholder="Search agents by name or email…" .value=${this._memberQuery} @input=${this._searchMember} />
              ${this._memberResults.length ? html`<div class="results">
                ${this._memberResults.map(u => html`<div @click=${() => this._addMember(u.id)}>${u.fullName || u.email}</div>`)}
              </div>` : ''}
            </div>
            <div class="field">
              <button class="danger" @click=${this._archive}>Archive group</button>
            </div>
          </div>` : html`<div class="card empty">Select a group to manage its members.</div>`}
      </div>
    `
  }
}

customElements.define('page-groups', PageGroups)
